import React, { useState, useEffect } from 'react'
import { Sun, Moon } from 'lucide-react'

// Cookie utilities
const setCookie = (name: string, value: string, days: number = 365) => {
  const expires = new Date()
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`
}

const getCookie = (name: string): string | null => {
  const nameEQ = name + "="
  const ca = document.cookie.split(';')
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === ' ') c = c.substring(1, c.length)
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
  }
  return null
}

type Theme = 'light' | 'dark'

interface ThemeSwitcherProps {
  className?: string
}

export const ThemeSwitcher: React.FC<ThemeSwitcherProps> = ({ className = '' }) => {
  const [theme, setTheme] = useState<Theme>('dark')

  // Load theme from cookies and localStorage on mount
  useEffect(() => {
    const loadTheme = () => {
      // Check for LCARS mode first
      const lcarsMode = localStorage.getItem('lcars-mode')
      if (lcarsMode === 'true') {
        document.body.classList.add('lcars-mode')
      }

      // First try to load from cookies
      const cookieTheme = getCookie('theme')
      let savedTheme = null

      if (cookieTheme && (cookieTheme === 'light' || cookieTheme === 'dark')) {
        savedTheme = cookieTheme as Theme
      }

      // Fallback to localStorage if no cookie
      if (!savedTheme) {
        const localStorageTheme = localStorage.getItem('theme')
        if (localStorageTheme && (localStorageTheme === 'light' || localStorageTheme === 'dark')) {
          savedTheme = localStorageTheme as Theme
        }
      }

      if (savedTheme) {
        setTheme(savedTheme)
        applyTheme(savedTheme)
      } else {
        // Default dark (matches the index.html no-flash script)
        setTheme('dark')
        applyTheme('dark')
      }
    }

    // Ensure DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', loadTheme)
    } else {
      loadTheme()
    }

    return () => {
      document.removeEventListener('DOMContentLoaded', loadTheme)
    }
  }, [])

  const applyTheme = (newTheme: Theme) => {
    const root = document.documentElement

    // Apply dark/light mode
    if (newTheme === 'dark') {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }

    // Save to both localStorage and cookies
    localStorage.setItem('theme', newTheme)
    setCookie('theme', newTheme, 365)
  }

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light'
    setTheme(newTheme)
    applyTheme(newTheme)

    // Deactivate LCARS mode when user selects light/dark
    const isLcarsMode = document.body.classList.contains('lcars-mode')
    if (isLcarsMode) {
      document.body.classList.remove('lcars-mode')
      localStorage.removeItem('lcars-mode')
    }
  }

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={toggleTheme}
        className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
        title={theme === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}
      >
        {theme === 'light' ? (
          <Moon className="h-5 w-5" />
        ) : (
          <Sun className="h-5 w-5" />
        )}
      </button>
    </div>
  )
}
